import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TrophyIcon } from '@heroicons/react/24/outline';

import AnimationCircle from './AnimationCircle';
import { CardHeader } from './ui/Card';
import {
  fetchQualificationStats,
  selectQualificationStatsData,
  selectQualificationStatsLoading,
} from '../slices/reportsSlice';
import useSelectedResource from '../hooks/useSelectedResource';
import { leagueTier } from '../utils/statusTokens';

// Qualification rule (forecast/src/market/kpi.py:find_forecaster_league):
// a forecaster is unqualified when n_days_w_penalties > maxMissedDays.
// The threshold is data-driven (stats.max_missed_days, with a 5 fallback for
// historical records written before the field was added).
const MAX_MISSED_DAYS_FALLBACK = 5;

// Qualification data is shared across tracks (submission activity doesn't
// differ by track); fetch with a fixed track since the API requires the param.
// The result goes to its own store slot: the leaderboard on the same page reads
// monthlyStatsData for the selected track.
const QUALIFICATION_TRACK = 'deterministic';

export default function MonthlyPerformanceCard({ year, month, horizon }) {
  const dispatch = useDispatch();
  const selectedResource = useSelectedResource();
  const resourceId = selectedResource?.id;

  const stats = useSelector(selectQualificationStatsData);
  const statsLoading = useSelector(selectQualificationStatsLoading);

  useEffect(() => {
    if (!resourceId || horizon == null) return;
    dispatch(
      fetchQualificationStats({
        resourceId,
        year,
        month,
        track: QUALIFICATION_TRACK,
        horizonOffset: horizon,
      })
    );
  }, [dispatch, resourceId, year, month, horizon]);

  const isLoading = statsLoading && !stats;

  const header = (
    <CardHeader
      title="League qualification"
      description="Your eligibility for monthly league rewards, based on submission consistency."
      className="mb-3"
    />
  );

  if (horizon == null) {
    return (
      <div className="w-full">
        {header}
        <div className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
          No data for the selected period.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {header}

      {isLoading ? (
        <div className="flex items-center justify-center py-3">
          <AnimationCircle />
        </div>
      ) : !stats ? (
        <div className="py-6 text-center text-xs text-gray-400 dark:text-gray-500">
          No data for the selected period.
        </div>
      ) : (
        <LeagueQualificationView stats={stats} />
      )}
    </div>
  );
}

function LeagueQualificationView({ stats }) {
  const maxMissedDays = stats.max_missed_days ?? MAX_MISSED_DAYS_FALLBACK;
  const missedDays = stats.n_days_w_penalties ?? 0;
  const submittedDays = stats.days_with_submissions ?? 0;
  const daysInPeriod = stats.days_in_period ?? 0;
  const participationPct = stats.participation_rate != null
    ? Math.round(stats.participation_rate)
    : (daysInPeriod > 0 ? Math.round((submittedDays / daysInPeriod) * 100) : null);

  const leagueId = stats.league_id;
  const isUnqualified = leagueId === 'unqualified';
  const isQualified = leagueId && leagueId !== 'unqualified';
  const daysOverLimit = Math.max(0, missedDays - maxMissedDays);
  const daysRemainingBeforeLimit = Math.max(0, maxMissedDays - missedDays);

  let statusText;
  if (isUnqualified) {
    statusText = `Not eligible this month: ${daysOverLimit} day${daysOverLimit === 1 ? '' : 's'} over the ${maxMissedDays}-day allowance`;
  } else if (isQualified) {
    statusText = daysRemainingBeforeLimit > 0
      ? `Eligible: you can still miss ${daysRemainingBeforeLimit} day${daysRemainingBeforeLimit === 1 ? '' : 's'} this month`
      : `Eligible: no missed days left this month`;
  } else {
    statusText = `Keep submitting daily to stay eligible (up to ${maxMissedDays} missed days allowed)`;
  }

  const statusColor = isUnqualified
    ? 'text-red-600 dark:text-red-400'
    : isQualified
      ? 'text-green-700 dark:text-green-400'
      : 'text-gray-600 dark:text-gray-300';

  // One tier palette for both the trophy icon and the league name: brand
  // colour per qualified tier, red for unqualified, gray for pending.
  const tierColor = isQualified ? leagueTier(leagueId).text : statusColor;

  const participationColor = isUnqualified
    ? 'bg-red-400'
    : participationPct != null && participationPct >= 85
      ? 'bg-green-500'
      : participationPct != null && participationPct >= 50
        ? 'bg-amber-500'
        : 'bg-gray-300 dark:bg-gray-600';

  const thresholdPct = daysInPeriod > 0
    ? Math.round(((daysInPeriod - maxMissedDays) / daysInPeriod) * 100)
    : null;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">
            Status
          </div>
          <div className="flex items-center gap-2">
            <TrophyIcon className={`h-5 w-5 ${tierColor}`} />
            <span className={`text-xl font-light tracking-tight ${tierColor}`}>
              {leagueTier(leagueId).label}
            </span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
            Missed days
          </div>
          <div className="text-xl font-light tracking-tight text-gray-900 dark:text-gray-100 font-mono tabular-nums">
            {missedDays}
            <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
              {' '}/ {maxMissedDays}
            </span>
          </div>
        </div>
      </div>

      <p className={`text-xs mb-2 ${statusColor}`}>{statusText}</p>

      {participationPct != null && (
        <div>
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono tabular-nums">
                {participationPct}%
              </span>{' '}
              participation
            </span>
            <span className="text-gray-400 dark:text-gray-500 tabular-nums">
              {submittedDays} / {daysInPeriod} day{daysInPeriod === 1 ? '' : 's'}
            </span>
          </div>
          <div className="relative h-2 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${participationColor}`}
              style={{ width: `${Math.min(participationPct, 100)}%` }}
            />
            {thresholdPct != null && thresholdPct > 0 && thresholdPct < 100 && (
              <div
                className="absolute top-0 bottom-0 w-px bg-gray-700 dark:bg-gray-300"
                style={{ left: `${thresholdPct}%` }}
                aria-hidden="true"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
